import { useEffect } from 'react';

export const UNSAVED_MESSAGE = 'You have unsaved changes. Leave this page and lose them?';

/** Mark a link or button that stays on the guarded page (such as a tab) so the guard lets it through. */
export const GUARD_SAFE_ATTR = 'data-unsaved-safe';

/**
 * Warns before leaving a page that holds unsaved edits.
 *
 * The app runs on a plain BrowserRouter, which has no navigation blocker, so
 * this covers the three ways out by hand: closing or reloading the tab
 * (beforeunload), the browser Back button (popstate; a declined Back puts the
 * address back), and a click on an in-app link or on the sidebar and header
 * buttons and the previous / next record arrows (caught before the router sees it). Anything marked with
 * data-unsaved-safe is let through.
 */
export function useUnsavedChangesGuard(dirty: boolean, message: string = UNSAVED_MESSAGE): void {
  useEffect(() => {
    if (!dirty) return undefined;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const onClickCapture = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const el = target?.closest?.('a[href], button');
      if (!el || el.closest(`[${GUARD_SAFE_ATTR}]`)) return;
      const leaves = el.matches('a[href]')
        ? !(el as HTMLAnchorElement).hasAttribute('download') && (el as HTMLAnchorElement).target !== '_blank'
        : !!el.closest('.sidebar, .global-header, .header-record-nav');
      if (!leaves) return;
      if (!window.confirm(message)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const here = window.location.href;
    const onPopState = () => {
      if (window.confirm(message)) return;
      window.history.pushState(window.history.state, '', here);
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClickCapture, true);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('popstate', onPopState);
    };
  }, [dirty, message]);
}
